/** Decode one optional-catch-all route segment without rejecting old URLs. */
export function decodeWorkspaceFilePathSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

export function workspaceFilesPathFromParams(
  orgSlug: string,
  filePath: string[] | undefined
): string {
  const rawPath = (filePath ?? [])
    .map(decodeWorkspaceFilePathSegment)
    .join('/');
  const withoutLeadingSlash = rawPath.replace(/^\/+/, '');
  if (!withoutLeadingSlash) return `/${orgSlug}`;
  if (
    withoutLeadingSlash === orgSlug ||
    withoutLeadingSlash.startsWith(`${orgSlug}/`)
  ) {
    return `/${withoutLeadingSlash}`;
  }
  return `/${orgSlug}/${withoutLeadingSlash}`;
}

export function workspaceFilesHref(orgSlug: string, path: string): string {
  const normalizedPath = path.replace(/^\/+/, '');
  const orgPrefix = `${orgSlug}/`;
  const routePath =
    normalizedPath === orgSlug
      ? ''
      : normalizedPath.startsWith(orgPrefix)
        ? normalizedPath.slice(orgPrefix.length)
        : normalizedPath;
  const encodedPath = routePath
    .split('/')
    .filter(Boolean)
    .map((segment) =>
      segment.startsWith('@')
        ? `@${encodeURIComponent(segment.slice(1))}`
        : encodeURIComponent(segment)
    )
    .join('/');
  return `/${orgSlug}/files/${encodedPath}`;
}

export function isLegacyWorkspaceSharedRoute(
  filePath: string[] | undefined
): boolean {
  return (
    filePath?.length === 1 &&
    decodeWorkspaceFilePathSegment(filePath[0] ?? '') === 'shared'
  );
}

export function isWorkspaceDrivesRootPath(
  orgSlug: string,
  path: string
): boolean {
  return path === `/${orgSlug}/drives`;
}

/** Keep the permissions inspector scoped to the item that opened it. */
export function workspacePermissionsPanelIsOpen(
  openPath: string | null,
  requestedPath: string
): boolean {
  return openPath === requestedPath;
}

/** Bare and pre-username `/users` entry routes both open the current home. */
export function shouldRedirectWorkspaceFilesToHome(
  filePath: string[] | undefined
): boolean {
  return (
    !filePath ||
    filePath.length === 0 ||
    (filePath.length === 1 &&
      decodeWorkspaceFilePathSegment(filePath[0] ?? '') === 'users')
  );
}

/** Select one Finder-rail location, preferring the deepest matching path. */
export function activeWorkspaceFilesRailPath(
  requestedPath: string,
  targetPaths: ReadonlyArray<string>
): string | null {
  let activePath: string | null = null;
  for (const targetPath of targetPaths) {
    if (
      requestedPath !== targetPath &&
      !requestedPath.startsWith(`${targetPath}/`)
    ) {
      continue;
    }
    if (activePath === null || targetPath.length > activePath.length) {
      activePath = targetPath;
    }
  }
  return activePath;
}

/** Select one rail target, giving favorites precedence over the Home fallback. */
export function resolveWorkspaceFilesRailTarget({
  requestedPath,
  homePath,
  favoritePaths,
  workspacePaths,
  utilityPaths = [],
}: {
  requestedPath: string;
  homePath: string | null;
  favoritePaths: ReadonlyArray<string>;
  workspacePaths: ReadonlyArray<string>;
  utilityPaths?: ReadonlyArray<string>;
}): string | null {
  const favoritePath = activeWorkspaceFilesRailPath(
    requestedPath,
    favoritePaths
  );
  if (favoritePath) return favoritePath;
  if (
    homePath &&
    activeWorkspaceFilesRailPath(requestedPath, [homePath]) === homePath
  ) {
    return homePath;
  }
  const utilityPath = activeWorkspaceFilesRailPath(requestedPath, utilityPaths);
  if (utilityPath) return utilityPath;
  return activeWorkspaceFilesRailPath(requestedPath, workspacePaths);
}
